import React, { FunctionComponent, ChangeEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton } from 'components';
import {
  InputFormatQuestion,
  InputQuestion,
} from 'scenes/common/components/Questions';
import { SingleFieldStepProps } from 'scenes/common/types';

import { validateABN } from './service/abn';

const MAX_NAME_LENGTH = 60;
const VALID_NAME_PATTERN = /^[A-Za-z0-9 \-,\\/\\\\\\'\\`]*$/;

export const BUSINESS_NAME_QUESTION = 'What is the business name?';
export const ABN_QUESTION = 'What is the ABN?';
export const BUSINESS_NAME_LABEL = 'Business Name';
export const ABN_LABEL = 'ABN';
export const ABN_HELP_TEXT = 'We only accept Australian Business Numbers';
export const ABN_ERROR_MESSAGE = 'Please enter a valid ABN';

const BUSINESS_NAME = 'businessName';
const ABN = 'abn';

const BusinessNameAndAbnStep: FunctionComponent<
  Pick<SingleFieldStepProps<string>, 'onNext'> & {
    defaultBusinessName: string;
    defaultAbn: string;
  }
> = ({ onNext, defaultBusinessName, defaultAbn }) => {
  const { formState: hooksForm, handleSubmit, errors, control } = useForm({
    mode: 'onChange',
  });

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (!value || value.match(VALID_NAME_PATTERN)) {
        onChange(value);
      }
    },
    []
  );

  return (
    <form onSubmit={handleSubmit(onNext)} autoComplete="on">
      <InputQuestion
        errors={errors}
        control={control}
        stateKey={BUSINESS_NAME}
        defaultValue={defaultBusinessName}
        maxLength={MAX_NAME_LENGTH}
        changeHandler={handleChange}
        autoCompleteName="organization"
        validationRules={{ required: true }}
        ariaLabel={BUSINESS_NAME_LABEL}
        question={BUSINESS_NAME_QUESTION}
        ariaRequired
      />

      <InputFormatQuestion
        ariaLabel={ABN_LABEL}
        size={4}
        errors={errors}
        format="## ### ### ###"
        control={control}
        stateKey={ABN}
        question={ABN_QUESTION}
        helpText={ABN_HELP_TEXT}
        defaultValue={defaultAbn}
        ariaRequired
        isNumericString
        validationRules={{
          validate: {
            validABN: (value) => validateABN(value) || ABN_ERROR_MESSAGE,
          },
        }}
      />
      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default BusinessNameAndAbnStep;
